/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { DatePipe } from '@angular/common'
import { Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { ActivatedRoute, Router } from '@angular/router'
import { ClassificacaoExaminadorDTO } from 'src/app/models/ClassificacaoExaminadorDTO'
import { ConceitoJustificativaDialogComponent } from './justificativa-dialog/conceito-justificativa-dialog.component';
import { JustificativaInfoDialogComponent } from './justificativa-info-dialog/justificativa-info-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ArquivoViewerDialogComponent } from '../../arquivo-viewer-dialog/arquivo-viewer-dialog.component';
import { firstValueFrom } from 'rxjs';
import { TipoConcurso } from 'src/app/enums/tipo-concurso';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

const JUSTIFICATIVA_EMPATE_TECNICO = 'Empate técnico - não foi possível desempatar pelas regras estabelecidas'

@Component({
  selector: 'inscricoes-lista-prova-examinador-unicamp',
  templateUrl: './lista-classificacao-examinador.component.html',
  styleUrls: ['./lista-classificacao-examinador.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class ListaClassificacaoExaminadorComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator
  @ViewChild(MatSort) sort!: MatSort

  colunasGrid: string[] = [];
  emailUsuario!: string;
  dataSource: MatTableDataSource<ClassificacaoExaminadorDTO> = new MatTableDataSource()
  datepipe: DatePipe = new DatePipe('pt-BR')
  tipoConcurso: string = '';
  instrucoesExpandidas: boolean = false;
  private _processandoConceito = false;

  constructor(
    private unespCoreMessageService: UnespCoreMessageService,
    private route: ActivatedRoute,
    private router: Router,
    private comissaoJulgadoraService: ComissaoJulgadoraService,
    public dialog: MatDialog  
  ) {}

  ngOnInit() {
    const userString = localStorage.getItem('sinsc__user');
    if (userString) {
      const user = JSON.parse(userString);
      this.emailUsuario = user?.identificacao ?? '';      
    }
      
    this.loadData();
  }

  loadData() {    
    const concursoId = Number(this.route.snapshot.paramMap.get('concursoId'));
    const fase = this.route.snapshot.queryParamMap.get('fase');
  
    if (!concursoId) {
      this.unespCoreMessageService.showMessageError('Concurso não encontrado na rota.');
      return;
    }

    // Se a fase não foi fornecida, busca do backend
    if (!fase) {
      this.comissaoJulgadoraService.listarProvasExaminador(concursoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (provas) => {
          if (provas.length > 0) {
            const faseDetectada = provas[0].concursoFase || 'FASE1';
            this.carregarClassificacao(concursoId, faseDetectada);
          } else {
            this.unespCoreMessageService.showMessageError('Nenhuma prova encontrada para o concurso.');
          }
        },
        error: (err) => {
          this.unespCoreMessageService.showMessageError(
            err?.error?.message ?? 'Erro ao buscar informações do concurso.'
          );
        }
      });
    } else {
      this.carregarClassificacao(concursoId, fase);
    }
  }

  carregarClassificacao(concursoId: number, fase: string) {
    this.comissaoJulgadoraService
      .obterClassificacaoExaminadorPorFase(concursoId, fase)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          // Extrai o tipo de concurso do primeiro item (quando backend enviar)
          if (data.length > 0 && data[0].tipoConcurso) {
            this.tipoConcurso = data[0].tipoConcurso;
          }

          const mappedData = data.map((item: any, index: number) => {            
            const nascimento = item.nascimento;
            const idade = this.calcularIdade(nascimento);
          
            return {
              maloteId: item.maloteId,
              classificacao: index + 1,
              inscricaoId: item.inscricaoId,
              conceito: item.conceito,
              nome: item.nome,
              nomeSocial: item.nomeSocial,
              concorrerVagaDeficiente: item.concorrerVagaDeficiente,
              jurado: item.jurado,
              documentoJuradoInvalido: item.documentoJuradoInvalido,
              provaFase: item.provaFase,
              estaEmpatado: item.estaEmpatado,
              existeClassificacao: item.existeClassificacao,
              justificativa: item.justificativa,
              nascimento: this.formatarData(nascimento) + ` (${idade} anos)`,
              idade: idade,
              mediaFinal: item.mediaFinal,
              provas: item.notasProvas.map((p: any) => ({
                nomeProva: p.tipoProva,
                nota: p.nota
              }))
            };
          });
        
          this.dataSource.data = mappedData;
        
          const provas = mappedData[0]?.provas.map((p: any) => p.nomeProva) || [];          
        
          const colunas = [
            'classificacao',
            'inscricaoId',
            'nome',        
            'nascimento',
            ...provas,
            'mediaFinal'
          ];
          
          if (mappedData[0]?.provaFase === 'FASE2') {
            colunas.push('conceito');
          }
          
          this.colunasGrid = colunas;
        },
        error: (err) => {
          this.unespCoreMessageService.showMessageError(
            err?.error?.message ?? 'Erro ao carregar a lista de concursos.'
          );          
        }
      });
  }

  enviarConceito(row: ClassificacaoExaminadorDTO) {
    if (this._processandoConceito) return;
    if (row.conceitoAnterior == row.conceito) return;
  
    const conceito = (row.conceito || '').trim().toUpperCase();

    // Limpar conceito (valor vazio): envia direto ao backend sem justificativa
    if (!conceito) {
      if (!row.conceitoAnterior) return;
      const payload = { conceito: '', justificativa: JUSTIFICATIVA_EMPATE_TECNICO };
      this.comissaoJulgadoraService.atualizarConceito(row.maloteId, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.unespCoreMessageService.showMessageSuccess('Conceito removido.');
          this.loadData();
        },
        error: (err) => {
          row.conceito = row.conceitoAnterior ?? null;
          const message = err?.error?.message || 'Erro ao remover conceito.';
          this.unespCoreMessageService.showMessageError(message);
        },
      });
      return;
    }
  
    if (conceito.length !== 1 || !/^[A-Z]$/.test(conceito)) {
      this.unespCoreMessageService.showMessageError('Informe uma letra válida (A-Z) para o conceito.');
      row.conceito = row.conceitoAnterior ?? undefined;
      return;
    }

    // Filtra todos os candidatos empatados com a mesma média (incluindo o atual)
    const candidatosEmpatados = this.dataSource.data.filter(
      r => r.estaEmpatado && r.mediaFinal === row.mediaFinal
    );

    // Valida se o conceito está dentro do limite permitido
    const totalEmpatados = candidatosEmpatados.length;
    const letraMaximaPermitida = String.fromCharCode(64 + totalEmpatados); // A=65, então 64+1=A
    
    if (conceito > letraMaximaPermitida) {
      this.unespCoreMessageService.showMessageError(
        `Existem apenas ${totalEmpatados} candidato(s) empatado(s) com esta nota. ` +
        `Os conceitos permitidos são de A até ${letraMaximaPermitida}.`,
        'Fechar'
      );
      row.conceito = row.conceitoAnterior ?? undefined;
      return;
    }

    const conceitoJaUsado = candidatosEmpatados.some(
      r => r !== row && r.conceito?.trim()?.toUpperCase() === conceito
    );

    if (conceitoJaUsado) {
      this.unespCoreMessageService.showMessageError(
        `O conceito '${conceito}' já foi atribuído para outro candidato com a mesma nota.`,
        'Fechar'        
      );      
      row.conceito = row.conceitoAnterior ?? null;
      return;
    }
  
    this._processandoConceito = true;
    const dialogRef = this.dialog.open(ConceitoJustificativaDialogComponent, {
      width: '1000px',
      data: { nome: row.nome },
      disableClose: true
    });
  
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((justificativa: string) => {
      this._processandoConceito = false;
      if (!justificativa) {
        row.conceito = row.conceitoAnterior ?? null;
        return;
      }
  
      const payload = { conceito, justificativa };
  
      this.comissaoJulgadoraService.atualizarConceito(row.maloteId, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.unespCoreMessageService.showMessageSuccess('Conceito atualizado com sucesso.');
          this.loadData();
        },
        error: (err) => {
          row.conceito = row.conceitoAnterior ?? null;
          const message = err?.error?.message || 'Erro ao atualizar conceito.';
          this.unespCoreMessageService.showMessageError(message);
          console.error(err);
        },
      });
    });
  }

  formatarData(dataStr: string): string {
    const data = new Date(dataStr);
    return this.datepipe.transform(data, 'dd/MM/yyyy') ?? '';
  }
  
  calcularIdade(dataStr: string): number {
    const nascimento = new Date(dataStr);
    const hoje = new Date();
  
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
  
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
  
    return idade;
  }

  getNota(row: any, nomeProva: string): number | string {
    const prova = row.provas.find((p: any) => p.nomeProva === nomeProva);
    return prova?.nota ?? '-';
  }

  permitirSomenteLetra(event: KeyboardEvent) {
    const char = event.key;
    const regex = /^[a-zA-Z]$/;
    if (!regex.test(char)) {
      event.preventDefault();
    }
  }

  mostrarJustificativa(justificativa: string) {
    this.dialog.open(JustificativaInfoDialogComponent, {
      width: '800px',
      data: { justificativa }
    });
  }

  async visualizarDocumentoJurado(inscricaoId: number): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.comissaoJulgadoraService.downloadDocumentoComplementarJurado(inscricaoId)
      );
      
      const url = URL.createObjectURL(response);
      const titulo = `Documento do Jurado - Inscrição #${inscricaoId}`;
      
      this.dialog.open(ArquivoViewerDialogComponent, {
        data: { tituloDoArquivo: titulo, urlDoArquivo: url },
        width: '98vw',
        height: '96vh',
        maxWidth: '100vw',
        panelClass: 'fullheight-dialog',
      });
    } catch (error) {
      this.unespCoreMessageService.showMessageError(
        'Erro ao carregar documento do jurado. Verifique se o arquivo existe.'
      );
      console.error('Erro no download:', error);
    }
  }

  exibirIconePCD(): boolean {
    return this.tipoConcurso !== TipoConcurso.PTPP;
  }

  existemEmpatesNaoResolvidos(): boolean {
    return this.dataSource.data.length > 0 && 
           this.dataSource.data[0]?.provaFase === 'FASE2' &&
           this.dataSource.data.some(row => row.estaEmpatado && !row.conceito);
  }

  toggleInstrucoes(): void {
    this.instrucoesExpandidas = !this.instrucoesExpandidas;
  }

  voltar(): void {
    const concursoId = this.route.snapshot.paramMap.get('concursoId');
    this.router.navigate(['/comissao-julgadora/listar-provas', concursoId]);
  }
}
